import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { submitLearnerInterest } from "@/lib/api/founding-partners.functions";

export const Route = createFileRoute("/learners/join")({
  head: () => ({
    meta: [
      { title: "Join as a Learner — Nkadimeng Danny Legal & Compliance" },
      {
        name: "description",
        content:
          "Register your interest in our paralegal, LLB support, CV clinic, and SA LexPractiq legal experience programs. We'll email you next steps.",
      },
      { property: "og:title", content: "Join as a Learner" },
      {
        property: "og:description",
        content: "Show interest in NDLCS and SA LexPractiq learner programs.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: LearnersJoinPage,
});

const PROGRAM_OPTIONS = [
  "LLB Support (UNISA)",
  "Paralegal Diploma",
  "CV Clinic",
  "Legal Research & Writing",
  "SA LexPractiq — Simulations",
  "Candidate Attorney Prep",
];

function LearnersJoinPage() {
  const submit = useServerFn(submitLearnerInterest);
  const [form, setForm] = useState({
    first_name: "",
    surname: "",
    email: "",
    cell_number: "",
    id_number: "",
    message: "",
    source: "website_learner_form",
  });
  const [programs, setPrograms] = useState<string[]>([]);
  const [done, setDone] = useState(false);

  const mut = useMutation({
    mutationFn: () =>
      submit({
        data: {
          ...form,
          cell_number: form.cell_number || null,
          id_number: form.id_number || null,
          message: form.message || null,
          programs,
        } as never,
      }),
    onSuccess: () => {
      setDone(true);
      toast.success("Thanks — we'll be in touch by email shortly.");
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not submit"),
  });

  function toggle(p: string) {
    setPrograms((cur) =>
      cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p],
    );
  }

  if (done) {
    return (
      <main className="mx-auto max-w-2xl px-4 py-16 text-center">
        <h1 className="font-display text-3xl text-navy">You're on the list</h1>
        <p className="mt-3 text-muted-foreground">
          We've emailed a confirmation to <strong>{form.email}</strong>. Our team
          will reach out with next steps and how to activate your account.
        </p>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-2xl px-4 py-10">
      <header className="mb-6">
        <h1 className="font-display text-3xl text-navy">Join as a learner</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Tell us who you are and which programs you're interested in. We'll
          email you next steps and, when you're ready, an invite to activate
          your learner account on the portal.
        </p>
      </header>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!form.first_name || !form.surname || !form.email) {
            toast.error("First name, surname and email are required.");
            return;
          }
          mut.mutate();
        }}
        className="space-y-4 rounded-xl border border-border bg-card p-6"
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="First name" value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} required />
          <Field label="Surname" value={form.surname} onChange={(v) => setForm({ ...form, surname: v })} required />
        </div>
        <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Cell number" value={form.cell_number} onChange={(v) => setForm({ ...form, cell_number: v })} />
          <Field label="ID / student number (optional)" value={form.id_number} onChange={(v) => setForm({ ...form, id_number: v })} />
        </div>

        <div>
          <div className="mb-2 text-sm font-medium text-navy">
            Programs you're interested in
          </div>
          <div className="flex flex-wrap gap-2">
            {PROGRAM_OPTIONS.map((p) => {
              const on = programs.includes(p);
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => toggle(p)}
                  className={`rounded-full border px-3 py-1 text-xs ${
                    on
                      ? "border-navy bg-navy text-white"
                      : "border-input bg-background text-muted-foreground hover:border-navy/40"
                  }`}
                >
                  {p}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-navy">
            Anything you'd like us to know
          </label>
          <textarea
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            rows={4}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            placeholder="Current studies, employer, timing, etc."
          />
        </div>

        <button
          type="submit"
          disabled={mut.isPending}
          className="inline-flex items-center justify-center rounded-md bg-navy px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          {mut.isPending ? "Submitting…" : "Submit interest"}
        </button>
      </form>
    </main>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
}) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium text-navy">
        {label}
        {required ? <span className="text-destructive"> *</span> : null}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
      />
    </label>
  );
}
